import { Skeleton } from '@/components/UI/Skeleton'
import { useTranslation } from 'react-i18next'

type Props = {
  /**
   * テスト用の data-testid（デフォルト: `spot-map-skeleton`）。
   * SpotMap のチャンク読み込み中プレースホルダーとスポット取得中のオーバーレイが同時に存在し得るため、
   * 用途ごとに別の値を渡してテストから区別できるようにする。
   */
  testId?: string
  /**
   * 支援技術に「読み込み中」を伝えるか（デフォルト: false）。
   * false のときは aria-hidden にし、読み込み中であることは親（main の aria-busy）が伝える前提。
   * 親の状態で読み込み中を表現できない SpotMap のチャンク読み込み中プレースホルダーでは true にして、
   * role="status" + 視覚的に隠した文言で自身が伝える。
   */
  announceLoading?: boolean
}

/**
 * 地図表示エリア全面を覆うスケルトン。
 * スポット一覧（マップ + リスト / マップのみ）のローディング中と、
 * SpotMap（MapLibre）のチャンク読み込み中に表示する。
 *
 * 親要素（main）の `relative` に対して `absolute inset-0` で全面を覆う。
 * z-index は SpotMap（`relative z-0` のスタッキングコンテキスト）より上で、
 * メニュードロワー（z-index 1000）やダイアログより下に収まる値にする。
 * 地図と同じく角丸なし・白背景にして、取得完了時に地図へそのまま切り替わるようにする。
 */
export function SpotMapSkeleton({ testId = 'spot-map-skeleton', announceLoading = false }: Props) {
  const { t } = useTranslation()

  return (
    <div
      data-testid={testId}
      className="absolute inset-0 z-10 overflow-hidden bg-white"
      {...(announceLoading ? { role: 'status' } : { 'aria-hidden': true })}
    >
      {announceLoading && <span className="sr-only">{t('common.loading_content', '読み込み中')}</span>}
      {/* Skeleton の height prop は px のみのため、Tailwind の h-full で親の高さに追従させる */}
      <Skeleton variant="rect" className="h-full rounded-none" />
    </div>
  )
}
